import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Loader2, Save, Share2, RotateCcw } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../../lib/i18n';

const SharingTab = ({ shareSettings, setShareSettings, saving, onSave }) => {
  const templates = shareSettings.templates || {};
  const defaults = shareSettings.defaults || {};

  const setTemplate = (code, value) => {
    setShareSettings({ ...shareSettings, templates: { ...templates, [code]: value } });
  };

  return (
    <Card className="rounded-2xl border-[#E0D6C8]">
      <CardHeader>
        <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
          <Share2 className="w-5 h-5 text-[#5C4023]" />
          Share Message
        </CardTitle>
        <CardDescription>
          The message staff send when they share a client's resource list by text, WhatsApp or email.
          Use <span className="font-mono text-[#5C4023]">{'{link}'}</span> where the list link should appear.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl bg-[#F3EFE9] p-3 text-xs text-[#5A5A5A]">
          Sharing happens through the staff member's own phone (Messages, WhatsApp, Mail), so there is no
          per-message cost. The client receives a warm intro plus a link to their translated list.
        </div>

        {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
          <div key={code}>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm font-medium">{name}</Label>
              {defaults[code] && templates[code] !== defaults[code] && (
                <Button variant="ghost" size="sm" data-testid={`reset-share-template-${code}`}
                  onClick={() => setTemplate(code, defaults[code])}
                  className="h-7 text-xs text-[#C87D3E] hover:text-[#5C4023]">
                  <RotateCcw className="w-3 h-3 mr-1" />Reset
                </Button>
              )}
            </div>
            <Textarea
              data-testid={`share-template-${code}`}
              value={templates[code] || ''}
              onChange={(e) => setTemplate(code, e.target.value)}
              placeholder={defaults[code] || ''}
              className="rounded-xl"
              rows={3}
              dir={code === 'ar' ? 'rtl' : 'ltr'}
            />
          </div>
        ))}

        <Separator />

        <div>
          <Label className="text-sm font-medium">Signature (optional)</Label>
          <Textarea
            data-testid="share-signature-input"
            value={shareSettings.signature || ''}
            onChange={(e) => setShareSettings({ ...shareSettings, signature: e.target.value })}
            placeholder={"Example:\n617Reach Mobile Clinic\nQuestions? Call (617) 555-1234"}
            className="rounded-xl mt-1.5"
            rows={3}
          />
          <p className="text-xs text-[#888888] mt-1">Added to the end of every shared message, in every language.</p>
        </div>

        <Button onClick={onSave} disabled={saving} data-testid="save-share-btn"
          className="w-full bg-[#5C4023] hover:bg-[#4A331C] rounded-full h-12">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Share Message
        </Button>
      </CardContent>
    </Card>
  );
};

export default SharingTab;
